import {
  BatchUpdateVatPayload,
  LocalisedValue,
  OfferOverrides,
  OfferWithSelectionAndAuthor,
  OfferingApiBatchUpdateOverridesPayload,
  OfferingApiUpdateOffersPayload,
  OfferingPlatformScope,
  SelectionOverridesResponse,
} from 'modules/shared-menu/types';

import { Offer } from '../../../types';

type BuildBatchEnableDisableOfferPayloadsArgs = {
  enabled: boolean;
  offers: Offer[];
  selectionId: string;
  userId: string;
};

export const buildBatchEnableDisableOfferPayloads = ({
  enabled,
  offers,
  selectionId,
  userId,
}: BuildBatchEnableDisableOfferPayloadsArgs): OfferWithSelectionAndAuthor[] => {
  return offers.map(offer => {
    const { scope, ...offerWithoutScope } = offer;

    return {
      ...offerWithoutScope,
      selection_id: selectionId,
      enabled: {
        ...offerWithoutScope.enabled,
        enabled,
      },
      author: {
        kind: 'user',
        id: userId,
      },
    };
  });
};

type BuildBatchEnableDisableOfferOverridesPayloadsArgs = {
  enabled: boolean;
  selectedIds: Set<string>;
  selectionOverrides?: SelectionOverridesResponse;
};

export const buildBatchEnableDisableOfferOverridesPayloads = ({
  enabled,
  selectedIds,
  selectionOverrides,
}: BuildBatchEnableDisableOfferOverridesPayloadsArgs): OfferingApiBatchUpdateOverridesPayload['offer_overrides_entries'] => {
  return [...selectedIds].map(offerId => {
    const maybeExistingOverrides: OfferOverrides = selectionOverrides?.offers?.[offerId] ?? {};
    return {
      offer_id: offerId,
      offer_overrides: {
        ...maybeExistingOverrides,
        enabled: { ...(maybeExistingOverrides.enabled ?? {}), enabled },
      },
    };
  });
};

export const buildResetOfferOverridesPayloads = (
  selectedIds: Set<string>,
): OfferingApiBatchUpdateOverridesPayload['offer_overrides_entries'] => {
  return [...selectedIds].map(offerId => {
    return {
      offer_id: offerId,
      offer_overrides: {},
    };
  });
};

export const buildBatchVatChangeOfferPayloads = (
  vatPayload: BatchUpdateVatPayload,
  offers: Offer[],
  selectionId: string,
  userId: string,
): OfferWithSelectionAndAuthor[] => {
  return offers.map(offer => {
    const { scope, ...offerWithoutScope } = offer;
    return {
      ...offerWithoutScope,
      ...vatPayload,
      selection_id: selectionId,
      author: {
        kind: 'user',
        id: userId,
      },
    };
  });
};

function isLocalisedValue(value: any): value is LocalisedValue {
  return (
    typeof value === 'object' &&
    typeof value.lang === 'string' &&
    typeof value.value === 'string' &&
    typeof value.verified === 'boolean'
  );
}

export const filterOutEmptyTranslations = (offer: Offer) =>
  Object.entries(offer).reduce((acc, [key, value]) => {
    if (Array.isArray(value) && value.every(isLocalisedValue)) {
      const newValue = value.filter((translation: LocalisedValue) => translation.value);
      return { ...acc, [key]: newValue };
    }
    return { ...acc, [key]: value };
  }, {} as Offer);

export const buildUpdateOffersPayload = (
  offers: Offer[],
  selectionId: string,
  userId: string,
): OfferWithSelectionAndAuthor[] => {
  return offers.map(offer => {
    const { scope, ...offerWithoutScope } = filterOutEmptyTranslations(offer);
    return {
      ...offerWithoutScope,
      selection_id: selectionId,
      author: {
        kind: 'user',
        id: userId,
      },
    };
  });
};

export const buildBatchUpdatePayload = (
  offerPayloads: OfferWithSelectionAndAuthor[],
  entityScope: OfferingPlatformScope,
): OfferingApiUpdateOffersPayload => {
  return {
    offers: offerPayloads,
    request_scope: entityScope,
  };
};

type BuildBatchAvailabilityVisibilityChangePayloadArgs = {
  weeklyVisibility: Offer['weekly_visibility'];
  weeklyAvailability: Offer['weekly_availability'];
  offers: Offer[];
  selectionId: string;
  userId: string;
};

export const buildBatchAvailabilityVisibilityChangePayload = ({
  weeklyVisibility,
  weeklyAvailability,
  offers,
  selectionId,
  userId,
}: BuildBatchAvailabilityVisibilityChangePayloadArgs): OfferWithSelectionAndAuthor[] => {
  return offers.map(offer => {
    const { scope, ...offerWithoutScope } = offer;
    return {
      ...offerWithoutScope,
      weekly_availability: weeklyAvailability,
      weekly_visibility: weeklyVisibility,
      selection_id: selectionId,
      author: {
        kind: 'user',
        id: userId,
      },
    };
  });
};
